import React from 'react';

import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

interface Member {
  user_uuid: string;
  first_name: string;
  last_name: string;
  company_role: string;
  photo_url: string;
  company_uuid: string;
}

interface CompanyMembersModalProps {
  members: Member[];
  open: boolean;
  onClose: () => void;
}

const CompanyMembersModal: React.FC<CompanyMembersModalProps> = ({ members, open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const sortedMembers = [...members].sort((a, b) => {
    const aName = `${a.first_name} ${a.last_name}`.toLowerCase();
    const bName = `${b.first_name} ${b.last_name}`.toLowerCase();
    return aName.localeCompare(bName);
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: isMobile ? 0 : 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          TODOS MEMBROS DA EMPRESA
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={1} mt={2}>
          {sortedMembers.map((member, index) => {
            return (
              <Box
                key={member.user_uuid}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.default,
                  borderLeft: `2px solid ${theme.palette.primary.main}`,
                  '&:hover': {
                    backgroundColor: theme.palette.background.default,
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s ease',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Avatar
                  src={member.photo_url}
                  alt={`${member.first_name} ${member.last_name}`}
                  sx={{
                    width: 48,
                    height: 48,
                  }}
                >
                  {member.first_name.charAt(0)}
                  {member.last_name.charAt(0)}
                </Avatar>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" color="text.primary">
                    {member.first_name} {member.last_name}
                  </Typography>
                  <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                    {member.company_role}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            px: 3,
            py: 1.5,
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: 2,
            backgroundColor: 'transparent',
            color: theme.palette.primary.main,
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 500,
            transition: 'all 0.2s',
            width: '50%',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.background.paper,
            },
          }}
        >
          Fechar
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyMembersModal;
